// pages/public/AmbulanceRequestDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Ambulance,
  MapPin,
  User,
  Phone,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  Navigation,
  Truck,
  DollarSign,
  Star
} from 'lucide-react';
import ambulanceService from '../../services/ambulanceService';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AmbulanceRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    fetchRequestDetails();
    
    // Auto-refresh if request is active
    const interval = setInterval(() => {
      if (request && ['accepted', 'en_route_pickup', 'patient_loaded', 'en_route_dropoff'].includes(request.status)) {
        fetchRequestDetails();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [id, request]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      const response = await ambulanceService.getRequestById(id);
      
      if (response.success) {
        setRequest(response.data);
      } else {
        setError(response.message || 'Failed to load request details');
      }
    } catch (err) {
      console.error('Fetch request error:', err);
      setError('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!cancelReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }

    try {
      setCancelling(true);
      const response = await ambulanceService.cancelRequest(id, cancelReason);
      
      if (response.success) {
        setShowCancelDialog(false);
        fetchRequestDetails();
      } else {
        alert(response.message || 'Failed to cancel request');
      }
    } catch (err) {
      console.error('Cancel request error:', err);
      alert('Failed to cancel request');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      en_route_pickup: 'bg-blue-100 text-blue-800',
      arrived_pickup: 'bg-purple-100 text-purple-800',
      patient_loaded: 'bg-indigo-100 text-indigo-800',
      en_route_dropoff: 'bg-cyan-100 text-cyan-800',
      arrived_dropoff: 'bg-teal-100 text-teal-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle className="w-5 h-5" />;
    if (status === 'cancelled') return <XCircle className="w-5 h-5" />;
    if (status.includes('route')) return <Navigation className="w-5 h-5" />;
    return <Clock className="w-5 h-5" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canCancelRequest = () => {
    return request && ['pending', 'searching', 'accepted', 'en_route_pickup'].includes(request.status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner text="Loading request details..." />
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  {error || 'Request not found'}
                </h3>
                <button
                  onClick={() => navigate('/ambulances/my-requests')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Back to My Requests
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/ambulances/my-requests')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Back to My Requests</span>
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-red-600 to-orange-600 px-6 py-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h1 className="text-2xl font-bold mb-1">Request Details</h1>
                    <p className="text-red-100">ID: #{request._id.slice(-8)}</p>
                  </div>
                  <div className={`px-4 py-2 rounded-full font-medium flex items-center gap-2 ${getStatusColor(request.status)}`}>
                    {getStatusIcon(request.status)}
                    <span className="capitalize">{request.status.replace(/_/g, ' ')}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-red-100">Requested</p>
                    <p className="font-medium">{formatDate(request.requestedAt)}</p>
                  </div>
                  {request.completedAt && (
                    <div>
                      <p className="text-red-100">Completed</p>
                      <p className="font-medium">{formatDate(request.completedAt)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Patient Information */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-6 h-6 text-red-600" />
                Patient Information
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-medium text-gray-900">{request.patientInfo?.name}</p>
                </div>
                {request.patientInfo?.age && (
                  <div>
                    <p className="text-sm text-gray-600">Age</p>
                    <p className="font-medium text-gray-900">{request.patientInfo.age} years</p>
                  </div>
                )}
                {request.patientInfo?.gender && (
                  <div>
                    <p className="text-sm text-gray-600">Gender</p>
                    <p className="font-medium text-gray-900 capitalize">{request.patientInfo.gender}</p>
                  </div>
                )}
                {request.patientInfo?.phone && (
                  <div>
                    <p className="text-sm text-gray-600">Contact</p>
                    <a
                      href={`tel:${request.patientInfo.phone}`}
                      className="font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <Phone className="w-4 h-4" />
                      {request.patientInfo.phone}
                    </a>
                  </div>
                )}
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-600">Condition</p>
                  <p className="font-medium text-gray-900">{request.patientInfo?.condition}</p>
                </div>
                {request.patientInfo?.symptoms && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-600">Symptoms</p>
                    <p className="text-gray-900">{request.patientInfo.symptoms}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Locations */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-6 h-6 text-red-600" />
                Locations
              </h2>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 mb-1">Pickup Location</p>
                    <p className="text-gray-600">{request.pickupLocation?.address}</p>
                    {request.pickupLocation?.landmark && (
                      <p className="text-sm text-gray-500 mt-1">
                        Landmark: {request.pickupLocation.landmark}
                      </p>
                    )}
                    {request.pickupLocation?.instructions && (
                      <p className="text-sm text-gray-500 mt-1">
                        Instructions: {request.pickupLocation.instructions}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 mb-1">Destination</p>
                    <p className="text-gray-600">{request.dropoffLocation?.address}</p>
                    {request.dropoffLocation?.facilityName && (
                      <p className="text-sm text-gray-500 mt-1">
                        Facility: {request.dropoffLocation.facilityName}
                      </p>
                    )}
                  </div>
                </div>

                {request.estimatedDistance && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Navigation className="w-4 h-4" />
                      <span>Estimated Distance: {request.estimatedDistance.toFixed(1)} km</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ambulance & Driver */}
            {(request.ambulance || request.vehicle || request.driver) && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Truck className="w-6 h-6 text-red-600" />
                  Assigned Resources
                </h2>
                <div className="space-y-4">
                  {request.ambulance && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Ambulance Service</p>
                      <p className="font-medium text-gray-900">
                        {typeof request.ambulance === 'object' ? request.ambulance.name : 'Assigned'}
                      </p>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-4">
                    {request.vehicle && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Vehicle</p>
                        <p className="font-medium text-gray-900">
                          {typeof request.vehicle === 'object' 
                            ? request.vehicle.registrationNumber 
                            : 'Assigned'}
                        </p>
                        {typeof request.vehicle === 'object' && request.vehicle.vehicleType && (
                          <p className="text-sm text-gray-500">{request.vehicle.vehicleType}</p>
                        )}
                      </div>
                    )}
                    {request.driver && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Driver</p>
                        <p className="font-medium text-gray-900">
                          {typeof request.driver === 'object'
                            ? `${request.driver.firstName} ${request.driver.lastName}`
                            : 'Assigned'}
                        </p>
                        {typeof request.driver === 'object' && request.driver.phone && (
                          <a
                            href={`tel:${request.driver.phone}`}
                            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1 mt-1"
                          >
                            <Phone className="w-3 h-3" />
                            {request.driver.phone}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Timeline */}
            {request.statusHistory && request.statusHistory.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-red-600" />
                  Status Timeline
                </h2>
                <div className="space-y-4">
                  {request.statusHistory.map((status, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          {getStatusIcon(status.status)}
                        </div>
                      </div>
                      <div className="flex-1 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                        <p className="font-medium text-gray-900 capitalize">
                          {status.status.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-gray-600">
                          {formatDate(status.timestamp)}
                        </p>
                        {status.notes && (
                          <p className="text-sm text-gray-600 mt-1">{status.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing Card */}
            {request.pricing && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-red-600" />
                  Pricing Details
                </h3>
                <div className="space-y-3">
                  {request.pricing.baseFee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Base Fee</span>
                      <span className="font-medium">KES {request.pricing.baseFee.toLocaleString()}</span>
                    </div>
                  )}
                  {request.pricing.distanceFee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Distance Fee</span>
                      <span className="font-medium">KES {request.pricing.distanceFee.toLocaleString()}</span>
                    </div>
                  )}
                  {request.pricing.waitingFee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Waiting Fee</span>
                      <span className="font-medium">KES {request.pricing.waitingFee.toLocaleString()}</span>
                    </div>
                  )}
                  {request.pricing.additionalCharges && request.pricing.additionalCharges.length > 0 && (
                    <>
                      {request.pricing.additionalCharges.map((charge, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">{charge.description}</span>
                          <span className="font-medium">KES {charge.amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <div className="flex justify-between">
                      <span className="font-bold text-gray-900">Total Amount</span>
                      <span className="font-bold text-red-600 text-xl">
                        KES {request.pricing.totalAmount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  {request.payment && (
                    <div className="bg-gray-50 rounded-lg p-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Payment Status</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          request.payment.status === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {request.payment.status}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Priority & Type */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Request Info</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Request Type</p>
                  <p className="font-medium text-gray-900 capitalize">
                    {request.requestType?.replace(/_/g, ' ')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Priority</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    request.priority === 'critical' ? 'bg-red-100 text-red-800' :
                    request.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                    request.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {request.priority}
                  </span>
                </div>
              </div>
            </div>

            {/* Special Requirements */}
            {request.specialRequirements && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Special Requirements</h3>
                <div className="space-y-2">
                  {request.specialRequirements.oxygenRequired && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-gray-900">Oxygen Required</span>
                    </div>
                  )}
                  {request.specialRequirements.wheelchairAccess && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-gray-900">Wheelchair Access</span>
                    </div>
                  )}
                  {request.specialRequirements.stretcherRequired && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-gray-900">Stretcher Required</span>
                    </div>
                  )}
                  {request.specialRequirements.medicalEscort && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-gray-900">Medical Escort</span>
                    </div>
                  )}
                  {request.specialRequirements.notes && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Additional Notes</p>
                      <p className="text-sm text-gray-900">{request.specialRequirements.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                {request.ambulance && typeof request.ambulance === 'object' && request.ambulance.contact?.phone && (
                  <a
                    href={`tel:${request.ambulance.contact.phone}`}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-semibold flex items-center justify-center gap-2"
                  >
                    <Phone className="w-4 h-4" />
                    Call Ambulance
                  </a>
                )}
                
                {canCancelRequest() && (
                  <button
                    onClick={() => setShowCancelDialog(true)}
                    className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition font-semibold flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Cancel Request
                  </button>
                )}

                {request.status === 'completed' && !request.rating?.score && (
                  <button
                    onClick={() => navigate(`/ambulances/requests/${id}/rate`)}
                    className="w-full bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition font-semibold flex items-center justify-center gap-2"
                  >
                    <Star className="w-4 h-4" />
                    Rate Service
                  </button>
                )}
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Emergency
              </h4>
              <p className="text-sm text-red-700 mb-3">
                For life-threatening emergencies, call 911 immediately
              </p>
              <a
                href="tel:911"
                className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition font-semibold flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Call 911
              </a>
            </div>
          </div>
        </div>

        {/* Cancel Dialog */}
        {showCancelDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Cancel Request</h3>
              <p className="text-gray-600 mb-4">
                Are you sure you want to cancel this ambulance request? Please provide a reason.
              </p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows="3"
                placeholder="Reason for cancellation..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelDialog(false)}
                  disabled={cancelling}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-50"
                >
                  Keep Request
                </button>
                <button
                  onClick={handleCancelRequest}
                  disabled={cancelling}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {cancelling ? (
                    <>
                      <Clock className="w-4 h-4 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Cancel Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmbulanceRequestDetail;